import {
    Grid,
    RangeBlocks,
    Tooltip,
    Typography,
    useTheme,
} from '../../../../base';
import {
    createScoreRangeConfig,
    capitalizeFirstLetter,
    IJobApplicationRequirement,
    scaleScores,
    getBasicColorsObject,
    getColorByEvaluatedScore,
    IRangeBlocksValues,
    getHexColorWithOpacity,
} from '../../../../common';
import { FlexGrid, ScoreDot } from './SkillBar.styles';

interface Props {
    requirement: IJobApplicationRequirement;
    requirementLevel: string;
    scoreRangeConfig: ReturnType<typeof createScoreRangeConfig>;
    isEditable?: boolean;
    handleBlockChange?: (blockId: number, value: IRangeBlocksValues) => void;
}

const SkillBar = (props: Props) => {
    const {
        requirement,
        scoreRangeConfig,
        requirementLevel,
        isEditable = false,
        handleBlockChange,
    } = props;
    const userRequirementScores = requirement.user_requirement_scores ?? [];

    const lastUserRequirementScore =
        userRequirementScores.length > 0
            ? userRequirementScores[userRequirementScores.length - 1]
            : null;

    const aiRequirementScore = requirement.score;
    const userRequirementScore = lastUserRequirementScore?.score;

    const mainScore =
        userRequirementScore != null
            ? userRequirementScore
            : aiRequirementScore;

    const mainScaledScore = scaleScores(mainScore);

    const theme = useTheme();
    const baseColors = getBasicColorsObject(theme.palette);
    const mainColor = getColorByEvaluatedScore(
        scaleScores(mainScore),
        baseColors
    );
    const secondaryColor =
        userRequirementScore != null
            ? getHexColorWithOpacity(
                  getColorByEvaluatedScore(
                      scaleScores(aiRequirementScore),
                      baseColors
                  ),
                  50
              )
            : undefined;
    const requirementReason =
        lastUserRequirementScore?.reason || requirement.reason;

    return (
        <Grid item xs={12} sx={{ paddingBottom: theme.spacing(1) }}>
            <Grid container>
                <Grid item xs={12} sx={{ display: 'flex' }}>
                    <Typography variant="subtitle1">
                        {`${requirement.name} `}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            marginInline: theme.spacing(0.5),
                            alignContent: 'center',
                            color: theme.palette.grey[600],
                        }}
                    >
                        {`| ${capitalizeFirstLetter(requirementLevel)}`}
                    </Typography>
                </Grid>
                <FlexGrid
                    container
                    spacing={2}
                    alignItems="center"
                    wrap="nowrap"
                >
                    <Grid item xs={9}>
                        <Tooltip title={requirementReason}>
                            <div>
                                <RangeBlocks
                                    externalValue={{
                                        from: 1,
                                        to: mainScaledScore,
                                    }}
                                    secondaryExternalValue={
                                        userRequirementScore != null
                                            ? {
                                                  from: 1,
                                                  to: scaleScores(
                                                      aiRequirementScore
                                                  ),
                                              }
                                            : undefined
                                    }
                                    selectMaxOnly
                                    disabled={!isEditable}
                                    showSelectionText={false}
                                    fillColor={mainColor}
                                    secondaryFillColor={secondaryColor}
                                    onChange={(value) =>
                                        handleBlockChange?.(
                                            requirement.id,
                                            value
                                        )
                                    }
                                    useInternalValue={false}
                                />
                            </div>
                        </Tooltip>
                    </Grid>
                    <Grid
                        item
                        xs={3}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginInlineStart: theme.spacing(3),
                        }}
                    >
                        <ScoreDot color={mainColor} />
                        <Typography
                            variant="body2"
                            sx={{
                                color: mainColor,
                                // whiteSpace: 'nowrap',
                                paddingInlineEnd: theme.spacing(1),
                            }}
                        >
                            {scoreRangeConfig.getSingleLabel(mainScaledScore)}
                        </Typography>
                    </Grid>
                </FlexGrid>
            </Grid>
        </Grid>
    );
};

export default SkillBar;
